import { PapiComment } from '@wix/da-papi-types';
import get from 'lodash/get';
import history from '../redux/routing/history';
import { CommentableTypes } from '../Comment/redux/types';
import { PapiCommentWithChildren } from './types';
import { commentOffsetParamName } from './constants';
import ddt from '@wix/da-ddt';

const logger = ddt.createLogger('comments');

const PAGE_SIZE = 20;

export function getStreamId(
  commentableTypeId: number | CommentableTypes,
  itemId: number,
  parentId?: number
) {
  let id = `comments:${commentableTypeId}-${itemId}`;
  if (parentId !== undefined) {
    id = `${id}-${parentId}`;
  }
  return id;
}

export function flatThreadToTree(comments: PapiComment[], maxDepth: number) {
  // let's first find everyone's children
  const commentsById: {
    [commentId: number]: {
      comment: PapiComment;
      children: PapiComment[];
    };
  } = {};
  comments.forEach(comment => {
    if (!comment) {
      return;
    }

    commentsById[comment.commentId] = {
      comment,
      children: comments.filter(i => i && i.parentId === comment.commentId),
    };
  });

  let index = -1;

  // we need to recursively add children, brace yourselves
  function inlineChildrenOfChildren(
    comment: PapiComment,
    depth
  ): PapiCommentWithChildren {
    index += 1;
    const { children } = commentsById[comment.commentId];

    // safety hatch
    if (!children) {
      return { ...comment, indent: depth, index, isLeafComment: true };
    }

    const isLeafComment = children.length === 0 || depth >= maxDepth;

    // once we are on a leaf node, we only want to show newly posted replies
    const filteredChildren = isLeafComment
      ? // cheating a little bit here, the field is tacked on by the saga...
        children.filter(child => (child as any).isNew)
      : children;

    return {
      ...comment,
      indent: depth,
      index,
      isLeafComment,
      children: filteredChildren.map(child =>
        inlineChildrenOfChildren(child, depth + 1)
      ),
    };
  }

  // now let's find comments without parents present...
  const roots = comments.filter(
    comment => comment && (!comment.parentId || !commentsById[comment.parentId])
  );

  // ...and add their happy little trees
  const forest = roots.map(child => inlineChildrenOfChildren(child, 0));

  // aaand done, now that wasnt so bad was it
  return forest;
}

export function getCommentBoxId(
  commentableTypeId: CommentableTypes,
  commentableItemId: number,
  position: string
) {
  return `comment-${commentableTypeId}-${commentableItemId}-${position}`;
}

function commentToOffsetCode({ typeId, itemId, commentId }: PapiComment) {
  return `${typeId}:${itemId}:${commentId}`;
}

export function offsetCodeToCommentIds(offset: string) {
  const parts = offset.split(':').map(value => Number(value));

  if (parts.length < 3 || parts.some(value => Number.isNaN(value))) {
    return;
  }

  const [typeId, itemId, commentId] = parts;
  return { typeId, itemId, commentId };
}

export function getCommentCursorFromQuery(
  queryObject: { [key: string]: string },
  validationIds?: { typeId?: number; itemId?: number }
) {
  const offset = get(queryObject, commentOffsetParamName);
  const ids = offsetCodeToCommentIds(
    offset && !Array.isArray(offset) ? (offset as string) : ''
  );

  if (!ids || !ids.commentId) {
    return;
  }

  logger.log('Offset code found', ids);

  if (validationIds) {
    if (validationIds.typeId && ids.typeId !== validationIds.typeId) {
      return;
    }
    if (validationIds.itemId && ids.itemId !== validationIds.itemId) {
      return;
    }
  }

  return `${ids.commentId}`;
}

export function updateCommentOffset(comment?: PapiComment) {
  window.history.scrollRestoration = comment ? 'manual' : 'auto';
  const params = new URLSearchParams(history.location.search);
  params.delete(commentOffsetParamName);
  if (comment) {
    params.set(commentOffsetParamName, commentToOffsetCode(comment));
  }
  history.replace({
    ...history.location,
    search: params.toString(),
  });
  logger.log(
    'history replaced',
    comment ? commentToOffsetCode(comment) : 'cleared'
  );
}

export function updateCommentOffsetEveryPage(
  comment: PapiComment,
  commentIndex?: number
) {
  if (commentIndex !== undefined && commentIndex % PAGE_SIZE === 0) {
    updateCommentOffset(comment);
  }
}

export function isActiveThread(typeId: number, itemId: number) {
  const params = new URLSearchParams(history.location.search);
  const offsetCode = params.get(commentOffsetParamName);
  if (!offsetCode || Array.isArray(offsetCode)) {
    return false;
  }

  const queryIds = offsetCodeToCommentIds(offsetCode as string);
  if (!queryIds) {
    return false;
  }

  if (itemId !== queryIds.itemId || typeId !== queryIds.typeId) {
    return false;
  }

  return true;
}
